import React from 'react'

import {
  DataGrid,
  DataGridFilterMenu,
  DataGridKeyboardShortcuts,
  DataGridRowHeightMenu,
  DataGridSearchButton,
  DataGridViewMenu,
} from '@/components/data-grid'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { getFilterFn } from '@/lib/data-grid-filters'
import { cn } from '@/lib/utils'
import { getSmartProjectColumnVisibility } from '@/lib/project-grid-defaults'
import { useApi } from '@/hooks/useApi'
import { useDataGrid } from '@/hooks/use-data-grid'
import useProjectStore from '@/store/useProjectStore'

const DEFAULT_GRID_HEIGHT = 640

// NOTE: Keep this list intentionally small for now.
// We can expand later as we calibrate the “knobs” exposed to agents.
const STATUS_OPTIONS = [
  { label: 'Planned', value: 'planned' },
  { label: 'Active', value: 'active' },
  { label: 'Blocked', value: 'blocked' },
  { label: 'Done', value: 'done' },
]

function ownersToIds(owners) {
  if (!Array.isArray(owners)) return []
  return owners
    .map(o => o?.id)
    .filter(id => typeof id === 'number')
    .map(id => String(id))
}

function deriveOwnerOptions(projects) {
  const map = new Map()
  for (const p of projects || []) {
    for (const o of p?.owners || []) {
      if (!o?.id) continue
      map.set(String(o.id), {
        value: String(o.id),
        label: o.name || o.email || `User ${o.id}`,
      })
    }
  }
  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label))
}

/**
 * ProjectDataGrid
 *
 * - DiceUI/TanStack DataGrid implementation.
 * - Inline editing, selection, copy/paste, keyboard shortcuts, and filter/sort menus.
 * - Local-only persistence: updates are written back to Zustand store.
 */
const ProjectDataGrid = ({ className, height = DEFAULT_GRID_HEIGHT }) => {
  const api = useApi()
  const projects = useProjectStore(s => s.projects)
  const archivedProjects = useProjectStore(s => s.archivedProjects)
  const fetchProjects = useProjectStore(s => s.fetchProjects)
  const fetchArchivedProjects = useProjectStore(s => s.fetchArchivedProjects)
  const setProjects = useProjectStore(s => s.setProjects)
  const [view, setView] = React.useState('active')

  React.useEffect(() => {
    if (!api || view !== 'archived') return
    fetchArchivedProjects(api)
  }, [api, view, fetchArchivedProjects])

  const rows = view === 'archived' ? archivedProjects : projects

  const ownerOptions = React.useMemo(
    () => deriveOwnerOptions(rows),
    [rows],
  )

  // Provide a stable reference for data
  const data = React.useMemo(() => {
    return (rows || []).map(p => ({
      ...p,
      // Normalize owners into an array of string IDs for the multi-select cell.
      owners: ownersToIds(p.owners),
    }))
  }, [rows])

  const columns = React.useMemo(() => {
    return [
      {
        accessorKey: 'id',
        header: 'ID',
        enableSorting: true,
        // keep it compact but readable
        size: 60,
        filterFn: getFilterFn(),
        meta: {
          label: 'ID',
          cell: { variant: 'number' },
        },
      },
      {
        size: 50,
        accessorKey: 'due_date',
        header: 'Due date',
        enableSorting: true,
        filterFn: getFilterFn(),
        meta: {
          label: 'Due date',
          cell: { variant: 'date'},
        },
      },
      {
        accessorKey: 'category',
        header: 'Category',
        enableSorting: true,
        filterFn: getFilterFn(),
        meta: {
          label: 'Category',
          cell: { variant: 'short-text' },
        },
      },
      {
        accessorKey: 'topic',
        header: 'Topic',
        enableSorting: true,
        enableHiding: false,
        filterFn: getFilterFn(),
        meta: {
          label: 'Topic',
          cell: { variant: 'short-text' },
        },
      },
      {
        accessorKey: 'details',
        header: 'Details',
        enableSorting: false,
        filterFn: getFilterFn(),
        meta: {
          label: 'Details',
          cell: { variant: 'long-text' },
        },
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        enableSorting: false,
        filterFn: getFilterFn(),
        meta: {
          label: 'Notes',
          cell: { variant: 'long-text' },
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: true,
        filterFn: getFilterFn(),
        meta: {
          label: 'Status',
          cell: {
            variant: 'select',
            options: STATUS_OPTIONS,
          },
        },
      },
      {
        accessorKey: 'owners',
        header: 'Owners',
        enableSorting: false,
        filterFn: getFilterFn(),
        meta: {
          label: 'Owners',
          cell: {
            variant: 'multi-select',
            options: ownerOptions,
          },
        },
      },
      {
        accessorKey: 'collection',
        header: 'Collection',
        enableSorting: true,
        filterFn: getFilterFn(),
        meta: {
          label: 'Collection',
          cell: { variant: 'short-text' },
        },
      },
      {
        id: 'actions',
        header: () => null,
        size: 120,
        enableSorting: false,
        meta: {
          label: 'Actions',
        },
        cell: ({ row }) => (
          <Button
            size="xs"
            className="px-2 rounded-sm"
            variant={view === 'archived' ? 'default' : 'secondary'}
            disabled={!api}
            onClick={async () => {
              if (!api) return
              const id = row.original.id
              if (view === 'archived') {
                await api.unarchiveProject(id)
              } else {
                await api.archiveProject(id)
              }
              await fetchProjects(api)
              await fetchArchivedProjects(api)
            }}
          >
            {view === 'archived' ? 'Unarchive' : 'Archive'}
          </Button>
        ),
      },
    ]
  }, [api, fetchArchivedProjects, fetchProjects, ownerOptions, view])

  const onDataChange = React.useCallback(
    nextRows => {
      // Local-only persistence.
      // Preserve the original owners payload if possible, but keep a future-friendly path:
      // - We store owners as string IDs in the grid.
      // - Later, when we add persistence, we’ll translate IDs -> join table updates.
      const merged = (nextRows || []).map((row, idx) => {
        const original = projects?.[idx]

        // If owners are present as ids, keep them on the row.
        // We do *not* attempt to denormalize back into user objects yet.
        return {
          ...original,
          ...row,
          clientId: original?.clientId ?? row?.clientId,
          id: original?.id ?? row?.id,
        }
      })

      setProjects(merged)
    },
    [projects, setProjects],
  )

  const columnVisibility = React.useMemo(() => {
    return getSmartProjectColumnVisibility(data, {
      // keep the key “chat reference” fields visible
      alwaysShow: ['id', 'category', 'topic', 'due_date'],
      // these are noisy unless needed
      defaultHidden: ['details', 'notes', 'collection', 'status'],
      mostlyEmptyThreshold: 0.8,
    })
  }, [data])

  const grid = useDataGrid({
    data,
    columns,
    readOnly: true,
    autoFocus: true,
    enableSearch: true,
    enablePaste: false,
    enableColumnSelection: true,
    // Keep the initial UX sane. Users can change row height via menu.
    rowHeight: 'short',
    initialState: {
      sorting: [{ id: 'due_date', desc: false }],
      columnPinning: { left: ['due_date', 'category', 'topic'], right: [] },
      columnVisibility,
    },
    onDataChange,
  })

  return (
    <div className={cn('flex h-full min-h-0 flex-col gap-3', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <ButtonGroup className="mr-1">
            <Button
              size="sm"
              variant={view === 'active' ? 'secondary' : 'outline'}
              onClick={() => setView('active')}
            >
              Active
            </Button>
            <Button
              size="sm"
              variant={view === 'archived' ? 'secondary' : 'outline'}
              onClick={() => setView('archived')}
            >
              Archived
            </Button>
          </ButtonGroup>
          <DataGridSearchButton table={grid.table} />
          <DataGridFilterMenu table={grid.table} />
          <DataGridRowHeightMenu
            table={grid.table}
            rowHeight={grid.rowHeight}
            onRowHeightChange={grid.table.options.meta?.onRowHeightChange}
          />
        </div>
        <div className="flex items-center gap-2">
          <DataGridKeyboardShortcuts enableSearch />
          <DataGridViewMenu table={grid.table} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              // Soft reset: keep columns but clear filters/sorts.
              grid.table.resetColumnFilters()
              grid.table.resetSorting()
            }}>
            Reset
          </Button>
        </div>
      </div>

      <DataGrid
        {...grid}
        height={height}
        stretchColumns
        className="bg-background/50 [&_[data-slot=grid-cell-wrapper]]:text-xs [&_[data-slot=grid-header-cell]_button]:text-xs"
      />
    </div>
  )
}

export default ProjectDataGrid
